// 符號貼圖載入 — 沿用 Unity Art/Symbol 資源（來源 Data/SymbolData.asset 的 NormalSprite 欄位，見
// docs/UNITY_SOURCE_ANALYSIS.md）。可長條化的 6 個符號（WW/H1/H2/H3/H4/SS）用尺寸感知貼圖
// `{name}_1x{size}`（1x1=214×192、1x2=214×268、1x3=214×402、1x4=214×536，即原生高度＝134×N，
// 以 cellW/214 縮放即自動跨對格數，對應 Unity SymbolView 的 SetNativeSize 做法）；其餘 7 個恆 1 格符號用單一貼圖。

import { type Texture } from 'pixi.js'
import { ensureAtlases, loadTexture } from '../../framework/assets/spriteAtlas'
import { SYMBOLS, isTallCapable } from '../config/symbols'
import { MAX_SYMBOL_SIZE } from '../constants'

const BASE_URL = '/images/symbols'

/** 可長條化符號：symbolId → (size 1..4 → texture)。 */
let bigCache: ReadonlyMap<number, ReadonlyMap<number, Texture>> | null = null
/** 恆 1 格符號：symbolId → texture。 */
let smallCache: ReadonlyMap<number, Texture> | null = null
/** Wild 造型變體：Wild 替代 H2/H3/H4 連線時的專屬造型（highId 2/3/4 → size → texture）。 */
let wildVariantCache: ReadonlyMap<number, ReadonlyMap<number, Texture>> | null = null
/** 免費遊戲 Scatter 野狼分級造型：level(1..3) → (size 1..4 → texture)。對照 Unity SS 的 Lv1/2/3Sprite（S1/S2/S3）。 */
let scatterWolfCache: ReadonlyMap<number, ReadonlyMap<number, Texture>> | null = null

/** 有 Wild 造型變體的高分符號 id（已驗證：SymbolData.asset 僅 H2/H3/H4 的 HaveWildSprite=1，對應 W2/W3/W4）。 */
const WILD_VARIANT_HIGH_IDS: readonly number[] = [2, 3, 4]
/** 免費遊戲 Scatter 野狼分級的 level 值（對應美術 S1/S2/S3，已驗證自 SymbolData.asset 的 Lv1/2/3Sprite）。 */
const SCATTER_WOLF_LEVELS: readonly number[] = [1, 2, 3]

export async function loadSymbolTextures(): Promise<void> {
  if (bigCache !== null && smallCache !== null) return

  const big = new Map<number, Map<number, Texture>>()
  const small = new Map<number, Texture>()
  const variant = new Map<number, Map<number, Texture>>()
  const wolf = new Map<number, Map<number, Texture>>()
  const jobs: Promise<void>[] = []

  // 先取 symbols 合圖（55 張符號貼圖收斂成 1 筆請求）；缺合圖時 loadTexture 自動退回單張。
  await ensureAtlases('symbols')

  for (const highId of WILD_VARIANT_HIGH_IDS) {
    const sizeMap = new Map<number, Texture>()
    variant.set(highId, sizeMap)
    for (let size = 1; size <= MAX_SYMBOL_SIZE; size++) {
      jobs.push(
        loadTexture(`${BASE_URL}/W${highId}_1x${size}.webp`).then((texture) => {
          sizeMap.set(size, texture)
        }),
      )
    }
  }

  // 免費遊戲 Scatter 野狼分級圖（S1/S2/S3 各 1x1~1x4，尺寸同 SS）。
  for (const level of SCATTER_WOLF_LEVELS) {
    const sizeMap = new Map<number, Texture>()
    wolf.set(level, sizeMap)
    for (let size = 1; size <= MAX_SYMBOL_SIZE; size++) {
      jobs.push(
        loadTexture(`${BASE_URL}/S${level}_1x${size}.webp`).then((texture) => {
          sizeMap.set(size, texture)
        }),
      )
    }
  }

  for (const symbol of SYMBOLS) {
    if (isTallCapable(symbol.id)) {
      const sizeMap = new Map<number, Texture>()
      big.set(symbol.id, sizeMap)
      for (let size = 1; size <= MAX_SYMBOL_SIZE; size++) {
        jobs.push(
          loadTexture(`${BASE_URL}/${symbol.name}_1x${size}.webp`).then((texture) => {
            sizeMap.set(size, texture)
          }),
        )
      }
    } else {
      jobs.push(
        loadTexture(`${BASE_URL}/${symbol.name}.webp`).then((texture) => {
          small.set(symbol.id, texture)
        }),
      )
    }
  }

  await Promise.all(jobs)
  bigCache = big
  smallCache = small
  wildVariantCache = variant
  scatterWolfCache = wolf
}

/**
 * 免費遊戲 Scatter 的野狼分級造型貼圖（level 1~3、size 為跨格高度）。對照 Unity SymbolView.SetNormal 依
 * CellData.Level 選 Lv1/2/3Sprite（S1/S2/S3）。未載入或超界時回 null（呼叫端回退一般 SS 造型）。
 */
export function getScatterWolfTexture(level: number, size = 1): Texture | null {
  if (scatterWolfCache === null) return null
  const clampedLevel = Math.max(1, Math.min(SCATTER_WOLF_LEVELS.length, level))
  const sizeMap = scatterWolfCache.get(clampedLevel)
  if (sizeMap === undefined) return null
  const clampedSize = Math.max(1, Math.min(MAX_SYMBOL_SIZE, size))
  return sizeMap.get(clampedSize) ?? sizeMap.get(1) ?? null
}

/** Wild 替代 H2/H3/H4 連線時的專屬造型貼圖（size 為跨格高度）；highSymbolId 非 2/3/4 或未載入時回 null。 */
export function getWildVariantTexture(highSymbolId: number, size = 1): Texture | null {
  if (wildVariantCache === null) return null
  const sizeMap = wildVariantCache.get(highSymbolId)
  if (sizeMap === undefined) return null
  const clamped = Math.max(1, Math.min(MAX_SYMBOL_SIZE, size))
  return sizeMap.get(clamped) ?? sizeMap.get(1) ?? null
}

/** 取得符號貼圖；size 為跨格高度（1~4），僅對可長條化符號有意義，其餘忽略。 */
export function getSymbolTexture(symbolId: number, size = 1): Texture {
  if (bigCache === null || smallCache === null) {
    throw new Error('符號貼圖尚未載入，請先呼叫 loadSymbolTextures()')
  }

  const single = smallCache.get(symbolId)
  if (single !== undefined) return single

  const sizeMap = bigCache.get(symbolId)
  if (sizeMap === undefined) {
    throw new Error(`找不到符號 ${symbolId} 的貼圖`)
  }
  const clamped = Math.max(1, Math.min(MAX_SYMBOL_SIZE, size))
  const texture = sizeMap.get(clamped) ?? sizeMap.get(1)
  if (texture === undefined) {
    throw new Error(`找不到符號 ${symbolId} 尺寸 ${size} 的貼圖`)
  }
  return texture
}
